import type { ProductPlan } from '@/features/products/types';

export type DurationUnit = 'day' | 'week' | 'month' | 'year' | 'lifetime' | 'one_time';

export interface Duration {
  count: number;
  unit: DurationUnit;
}

export function isTrialPlan(plan: ProductPlan): boolean {
  return plan.recurringPrice != null && plan.recurringPrice !== plan.price;
}

/** Crossed-out price: always the catalog `prev_price` field. */
export function strikePrice(plan: ProductPlan): number | null {
  const prev = plan.previousPrice;
  if (prev == null || prev <= 0) return null;
  return prev;
}

/**
 * Strike shown on the single-plan "Total now" row: catalog previous price,
 * otherwise the post-trial recurring price when that is higher than today.
 */
export function totalNowStrike(plan: ProductPlan): number | null {
  const prev = strikePrice(plan);
  if (prev != null && prev > plan.price) return prev;
  if (isTrialPlan(plan)) {
    const recurring = plan.recurringPrice;
    if (recurring != null && recurring > plan.price) return recurring;
  }
  return null;
}

export function savingsPercent(plan: ProductPlan): number {
  if (plan.discountPercent && plan.discountPercent > 0) return Math.round(plan.discountPercent);
  const reference = strikePrice(plan);
  const current = displayPrice(plan);
  if (reference && reference > current) {
    return Math.round((1 - current / reference) * 100);
  }
  return 0;
}

export function parseDuration(
  code?: string,
  options: { collapse?: boolean } = {},
): Duration | null {
  const { collapse = true } = options;
  if (!code) return null;
  const c = code.toLowerCase().trim();
  if (c === 'lifetime') return { count: 1, unit: 'lifetime' };
  if (c.includes('otp')) return { count: 1, unit: 'one_time' };

  const value = Math.abs(Number(c.match(/-?\d+/)?.[0] ?? 0));
  if (!value) return null;

  if (/y/.test(c)) return { count: value, unit: 'year' };
  if (/m/.test(c)) {
    return collapse && value >= 12 && value % 12 === 0
      ? { count: value / 12, unit: 'year' }
      : { count: value, unit: 'month' };
  }
  if (/w/.test(c)) return { count: value, unit: 'week' };
  if (/d/.test(c)) {
    return collapse && value % 7 === 0
      ? { count: value / 7, unit: 'week' }
      : { count: value, unit: 'day' };
  }
  return null;
}

export function pluralizeUnit(unit: DurationUnit, count: number): string {
  if (unit === 'lifetime' || unit === 'one_time') return '';
  return count === 1 ? unit : `${unit}s`;
}

const UNIT_TITLE: Record<DurationUnit, string> = {
  day: 'Day',
  week: 'Week',
  month: 'Month',
  year: 'Year',
  lifetime: 'Lifetime',
  one_time: 'One-time',
};

export function durationLabel(duration: Duration | null): string {
  if (!duration) return '';
  const { count, unit } = duration;
  if (unit === 'lifetime') return 'Lifetime';
  if (unit === 'one_time') return 'One-time';
  if (count === 1) {
    return { day: 'Daily', week: 'Weekly', month: 'Monthly', year: 'Yearly' }[unit] ?? '';
  }
  return `Every ${count} ${pluralizeUnit(unit, count)}`;
}

export function trialLabel(plan: ProductPlan): string {
  const duration = parseDuration(plan.trialLengthCode, { collapse: false });
  if (!duration) return 'Trial';
  return `${duration.count}-${UNIT_TITLE[duration.unit]} Trial`;
}

export function trialDurationPhrase(plan: ProductPlan): string {
  const duration = parseDuration(plan.trialLengthCode, { collapse: false });
  return duration ? `${duration.count}-${duration.unit} ` : '';
}

export function planPeriodLabel(plan: ProductPlan): string {
  if (plan.interval === 'one_time') return 'One-time';
  if (isTrialPlan(plan)) return trialLabel(plan);
  return durationLabel(parseDuration(plan.lengthCode)) || plan.name;
}

export function renewalPhrase(plan: ProductPlan): string {
  if (plan.interval === 'one_time') return '';
  const duration = parseDuration(plan.lengthCode) ?? intervalToDuration(plan);
  if (!duration || duration.unit === 'lifetime' || duration.unit === 'one_time') return '';
  const { count, unit } = duration;
  return count === 1 ? `every ${unit}` : `every ${count} ${pluralizeUnit(unit, count)}`;
}

function intervalToDuration(plan: ProductPlan): Duration | null {
  switch (plan.interval) {
    case 'daily':
      return { count: 1, unit: 'day' };
    case 'weekly':
      return { count: 1, unit: 'week' };
    case 'monthly':
      return { count: 1, unit: 'month' };
    case 'yearly':
      return { count: 1, unit: 'year' };
    case 'lifetime':
      return { count: 1, unit: 'lifetime' };
    default:
      return null;
  }
}

/** Days used to derive a per-day price (matches Figma: 7 / 3 / 365). */
export function planDayCount(plan: ProductPlan): number {
  const code = isTrialPlan(plan) ? plan.trialLengthCode : plan.lengthCode;
  const duration = parseDuration(code, { collapse: false }) ?? intervalToDuration(plan);
  if (!duration) return 1;
  switch (duration.unit) {
    case 'day':
      return duration.count;
    case 'week':
      return duration.count * 7;
    case 'month':
      return duration.count * 30;
    case 'year':
      return duration.count * 365;
    default:
      return 1;
  }
}

export function perDayPrice(plan: ProductPlan): number {
  return plan.price / planDayCount(plan);
}

export function perDayStrike(plan: ProductPlan): number | null {
  const strike = strikePrice(plan);
  if (strike == null) return null;
  return strike / planDayCount(plan);
}

/** Month count used to show a per-month price (3m / 6m). Yearly (12m / 1y) stays 1. */
export function billedMonthCount(plan: ProductPlan): number {
  if (isTrialPlan(plan)) return 1;
  const duration = parseDuration(plan.lengthCode);
  if (duration?.unit === 'month' && duration.count > 1) return duration.count;
  return 1;
}

export function displayPrice(plan: ProductPlan): number {
  return plan.price / billedMonthCount(plan);
}

export function displayStrikePrice(plan: ProductPlan): number | null {
  return strikePrice(plan);
}

/** "/mo" after the unit price when the plan spans more than one month. */
export function pricePeriodSuffix(plan: ProductPlan): string | null {
  return billedMonthCount(plan) > 1 ? '/mo' : null;
}

/** Month-billed plans (1m / 3m / 6m). Yearly (12m / 1y) is not monthly. */
export function isMonthlyPlan(plan: ProductPlan): boolean {
  if (isTrialPlan(plan)) return false;
  return parseDuration(plan.lengthCode)?.unit === 'month';
}

/** Uppercase duration heading for the v2 plan cards, e.g. "1 MONTH" / "3 MONTHS". */
export function planPeriodHeading(plan: ProductPlan): string {
  const duration = parseDuration(plan.lengthCode);
  if (!duration) return plan.name.toUpperCase();
  if (duration.unit === 'lifetime') return 'LIFETIME';
  if (duration.unit === 'one_time') return 'ONE-TIME';
  const unit = duration.unit.toUpperCase();
  return `${duration.count} ${duration.count === 1 ? unit : `${unit}S`}`;
}
